/**
 * 佐賀県 名物めし 12段階（コンテンツデータ）。
 *
 * 数値（radius / mass / score / unlockScore）は他県と同じテンプレート。
 * 進行の軸は「物理的な大きさ ＝ ごちそう度」で、県ごとに触らない。
 *
 * 調査で出てきた「書いてはいけないこと」を反映している。
 *  - 「日本三大稲荷」（祐徳稲荷）は神社公式・鹿島市公式の両方が書いているが、
 *    組み合わせが全国で割れており国の裏づけがない。使わない。
 *  - 「日本三大朝市」（呼子）「日本三大美肌の湯」（嬉野）はいずれも根拠資料なし。
 *  - 佐賀のりの「生産量日本一」は兵庫と年ごとに入れ替わる。年次と指標を書けないので使わない。
 *  - シシリアンライスの発祥は佐賀市観光協会自身が「うわさによると」。店名も年も書かない。
 *  - 丸ぼうろの「元祖」は特定の店の記録。店名を書かない方針なので、伝来の経路だけにする。
 *  - 神埼そうめんの寛永12年、須古寿しの「500年以上前」はどちらも公式が伝承形。
 *  - 小城羊羹の「外側の砂糖がシャリっとする」は公式にない。「表面が固い」までにする。
 *  - だぶは佐賀だけの料理ではない。農水省は「佐賀県から福岡県にまたがって伝承」。
 *  - 佐賀牛は「佐賀県産の黒毛和牛」ではない。JAグループ佐賀管内で肥育され、
 *    肉質等級4・5かつ BMS7以上のものだけ。それ以外は「佐賀産和牛」。
 *  - 竹崎カニを「ガザミ」と書かない。太良町の表記は「竹崎カニ」で、
 *    「全国的にはワタリガニとして知られる」まで。
 *  - ムツゴロウは環境省レッドリストの絶滅危惧IB類。漁も料理も続いているので
 *    掲載は問題ないが、「たくさん獲れる」「食べ放題」的な書き方はしない。
 *
 * 本番画像は public/assets/foods/saga/ に置く。
 *
 * 2026-10-02 確定案（audit/_確定案.csv）で料理を入れ替えた。
 * - 外した品：丸ぼうろ・小城羊羹・神埼そうめん・須古寿し・だぶ（店で食べられる品に差し替え）
 * - ワラスボ焼き：「映画『エイリアン』のモデルになった」という話はネットに出るが公式で確かめられないので書かない（小城市は「エイリアンを想像させられる」とのみ書く）
 * - 武雄のちゃんぽん：特定の店の初代が考えたという話は店の宣伝になるので書かない
 * - 鯉のあらい（小城の鯉料理）：酢味噌を添えることは、開いた公式ページに書かれていないので書かない
 * - 鯉のあらい（小城の鯉料理）：「寒鯉が一年でいちばんおいしい」はネットに出るが公式で確かめられないので書かない
 * - 鯉のあらい（小城の鯉料理）：鯉料理組合のサイトは http のみなので使わない
 * - 「むつごろうの蒲焼」を「むつごろう料理」に改め、絵と説明を描き直した（同じ料理なので ID は mutsugoro-kabayaki のまま）。
 * - 竹崎カキ焼き：カキ小屋の営業期間は年によって変わるので書かない
 */

import { attachHulls } from '../../utils/itemShape';
import type { RewardLink, StageDefinition, StageItemDefinition } from '../types';

import { SAGA_FOOD_HULLS } from './sagaFoodShapes.generated';
import { sagaSceneryTour } from './sagaScenes';

/** 「食べに行く」導線の既定リンク（特定店の宣伝にならないよう検索リンクにする）。 */
function searchLinks(area: string, dish: string): RewardLink[] {
  return [
    {
      label: '地図で探す',
      url: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${area} ${dish}`)}`,
    },
    {
      label: 'お店を調べる',
      url: `https://www.google.com/search?q=${encodeURIComponent(`${area} ${dish}`)}`,
    },
  ];
}

const FOOD_ITEMS: StageItemDefinition[] = [
  {
    id: 'ichigosan',
    level: 1,
    name: 'いちごさん',
    shortName: 'いちごさん',
    image: '/assets/foods/saga/01_ichigosan.webp',
    radius: 17,
    mass: 1,
    score: 2,
    description: '佐賀が20年かけて選んだ一粒。',
    source: 'https://www.pref.saga.lg.jp/kiji00323249/index.html',
    icon: { label: 'いちごさん', shape: 'berry', baseColor: '#d8353f', accentColor: '#3f7a3a' },
    reward: {
      unlockScore: 200,
      area: '佐賀県内各地',
      headline: '佐賀県が育成したいちご。品種名は「佐賀i9号」で、2018年に品種登録された。',
      tip: '果皮の赤が濃く、形がそろう。名前は公募で決まった。',
      links: searchLinks('佐賀県', 'いちごさん'),
    },
  },
  {
    id: 'warasubo-yaki',
    level: 2,
    name: 'ワラスボ焼き',
    shortName: 'ワラスボ焼き',
    image: '/assets/foods/saga/02_warasubo_yaki.webp',
    radius: 21,
    mass: 1.3,
    score: 4,
    description: '有明海の泥にすむワラスボの干物をあぶる。紫色のうなぎのような体で、歯がむき出しの顔つき。目は退化して皮膚の下に隠れている。内臓を取って丸ごと干し、切って揚げたりあぶったりして食べる。',
    source:
      'https://www.jf-sariake.or.jp/page/maeumimon_warasubo.html',
    icon: { label: 'ワラスボ', shape: 'fish', baseColor: '#6e5a6e', accentColor: '#c98a4b' },
    reward: {
      unlockScore: 500,
      area: '有明海沿岸',
      headline: '牙をむいた有明海の魚の干物',
      tip: '日本では有明海だけにすむ珍しい魚。その顔つきはエイリアンを思わせるとされ、味噌汁や干物にして食べる。',
      links: searchLinks('佐賀市', 'ワラスボ 干物'),
      official: [{ label: '小城市「特産品紹介 海産物」', url: 'https://www.city.ogi.lg.jp/main/2410.html' }],
    },
  },
  {
    id: 'saga-ramen',
    level: 3,
    name: '佐賀ラーメン',
    shortName: '佐賀ラーメン',
    image: '/assets/foods/saga/03_saga_ramen.webp',
    radius: 26,
    mass: 1.7,
    score: 8,
    description: '九州の豚骨をルーツに、佐賀で独自に育った豚骨ラーメン。スープにぽってりとした生卵を落とし、中太のやわらかい麺に、香りの高い佐賀海苔を添える。',
    source:
      'https://saga-ramen.jp/',
    icon: { label: '佐賀ラーメン', shape: 'ramen', baseColor: '#f1e6cf', accentColor: '#f2a33a' },
    reward: {
      unlockScore: 1000,
      area: '佐賀市ほか県内各地',
      headline: '豚骨スープに生卵を落とす',
      tip: '博多とも久留米とも違う佐賀の豚骨とされる。いまは豚骨にとどまらず、醤油や味噌、塩、つけ麺まで広がっている。',
      links: searchLinks('佐賀市', '佐賀ラーメン'),
    },
  },
  {
    id: 'sicilian-rice',
    level: 4,
    name: '佐賀シシリアンライス',
    shortName: 'シシリアン',
    image: '/assets/foods/saga/04_sicilian_rice.webp',
    radius: 30,
    mass: 2.2,
    score: 16,
    description: 'ご飯の上に、サラダと肉。',
    source:
      'https://www.bunka.go.jp/seisaku/shokubunka/foodculture/hyakunenfood/jirei/list_saga.html',
    icon: { label: 'シシリアン', shape: 'dumpling', baseColor: '#d8894a', accentColor: '#6ea35a' },
    reward: {
      unlockScore: 1800,
      area: '佐賀市',
      headline: '文化庁「100年フード」認定。昭和50年ごろのまかない料理が起源とされる。',
      tip: '温かいご飯に生野菜と甘辛く炒めた肉と玉ねぎをのせ、マヨネーズをかける。',
      links: searchLinks('佐賀市', 'シシリアンライス'),
    },
  },
  {
    id: 'takeo-champon',
    level: 5,
    name: '武雄のちゃんぽん',
    shortName: '武雄ちゃんぽん',
    image: '/assets/foods/saga/05_takeo_champon.webp',
    radius: 36,
    mass: 2.8,
    score: 32,
    description: '炭鉱の町だった武雄市北方町で、炭鉱で働く人に安く早く、栄養もたっぷり食べられる料理として生まれた。海がないため、具はてんこ盛りの野菜にかまぼこと豚肉。スープは豚骨が基本。',
    source:
      'https://www.asobo-saga.jp/galleries/detail/c80c5395-84df-48ea-974b-f0cdee7632db',
    icon: { label: 'ちゃんぽん', shape: 'noodle-bowl', baseColor: '#efe6d2', accentColor: '#6aa84f' },
    reward: {
      unlockScore: 3000,
      area: '武雄市北方町',
      headline: '野菜をてんこ盛りにした炭鉱の味',
      tip: '北方の炭鉱は江戸中期に始まり、明治から昭和にかけて最盛期を迎えた。そこで働く男たちに、ボリュームたっぷりのちゃんぽんが好まれた。',
      links: searchLinks('武雄市北方町', '北方ちゃんぽん'),
      official: [{ label: '武雄市観光協会「ちゃんぽん」', url: 'https://www.takeo-kk.net/enjoy/chanpon.html' }],
    },
  },
  {
    id: 'ureshino-yudofu',
    level: 6,
    name: '嬉野温泉湯どうふ',
    shortName: '温泉湯どうふ',
    image: '/assets/foods/saga/06_ureshino_yudofu.webp',
    radius: 42,
    mass: 3.5,
    score: 64,
    description: '豆腐が、淡雪になる。',
    source:
      'https://www.bunka.go.jp/seisaku/shokubunka/foodculture/hyakunenfood/jirei/list_saga.html',
    icon: { label: '温泉湯どうふ', shape: 'hotpot', baseColor: '#f4efe4', accentColor: '#c8d8c0' },
    reward: {
      unlockScore: 4500,
      area: '嬉野市',
      headline: '嬉野の温泉水で豆腐を煮る。文化庁「100年フード」認定。',
      tip: 'ふわふわと淡雪のような食感になり、大豆の甘みが立つ。冷奴としても食べられる。',
      links: searchLinks('嬉野市', '温泉湯どうふ'),
    },
  },
  {
    id: 'ogi-koi-no-arai',
    level: 7,
    name: '鯉のあらい（小城の鯉料理）',
    shortName: '鯉のあらい',
    image: '/assets/foods/saga/07_ogi_koi_no_arai.webp',
    radius: 46,
    mass: 4.4,
    score: 128,
    description: 'おろした鯉を薄切りにし、流水やぬるま湯で軽く洗ってから、小城の店では氷で引き締める。鯉は餌を与えずに冷たい清流に1〜2か月ほどさらし、身を締めて臭みを抜いておく。',
    source:
      'https://www.ogi-kankou.com/page-1017',
    icon: { label: '鯉のあらい', shape: 'sashimi-platter', baseColor: '#f4e8e4', accentColor: '#f2d03b' },
    reward: {
      unlockScore: 6500,
      area: '小城市（清水の滝周辺）',
      headline: '清流にさらした鯉を氷で締める',
      tip: '清水の滝の水は全国名水百選に選ばれた清流。滝のそばに鯉料理店が並び、あらいや味噌で煮込む鯉こくが小城の名物として知られる。',
      links: searchLinks('小城市 清水の滝', '鯉のあらい'),
      official: [{ label: '小城市「清水の滝」', url: 'https://www.city.ogi.lg.jp/main/6226.html' }, { label: '佐賀県観光連盟 あそぼーさが「清水の滝」', url: 'https://www.asobo-saga.jp/spots/detail/e777de34-7b6b-46d4-8a21-5b906b2af8ba' }],
    },
  },
  {
    id: 'mutsugoro-kabayaki',
    level: 8,
    name: 'むつごろう料理',
    shortName: 'むつごろう料理',
    image: '/assets/foods/saga/08_mutsugoro_kabayaki.webp',
    radius: 51,
    mass: 5.4,
    score: 256,
    description: '有明海の干潟にすむムツゴロウを素焼きにし、砂糖、醤油、酒で煮汁がなくなるまでじっくり煮る。生きたものを素焼きにするので鮮度が大事。むつごろう寿司にもする。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/45_10_saga.html',
    icon: { label: 'むつごろう', shape: 'fish', baseColor: '#5b3a24', accentColor: '#c98a4b' },
    reward: {
      unlockScore: 9000,
      area: '有明海沿岸',
      headline: '干潟の魚を甘辛く煮る',
      tip: 'とれるのは5月過ぎから8月までで、この時期だけ食べられる。引き潮のときに「むつかけ漁」という伝統漁法で釣り上げる。',
      links: searchLinks('鹿島市', 'むつごろう 蒲焼'),
    },
  },
  {
    id: 'takezaki-kaki-yaki',
    level: 9,
    name: '竹崎カキ焼き',
    shortName: '竹崎カキ焼き',
    image: '/assets/foods/saga/09_takezaki_kaki_yaki.webp',
    radius: 57,
    mass: 6.6,
    score: 512,
    description: '太良町の冬の味覚、竹崎カキを殻ごと焼く。大きいものは一つ150gほど。身の水分が少なく、焼いても縮みにくいので、カキ本来の味が濃く感じられる。',
    source:
      'https://www.town.tara.lg.jp/kanko/_1899/_1904.html',
    icon: { label: 'カキ焼き', shape: 'sashimi-platter', baseColor: '#e9e2cf', accentColor: '#7d7a72' },
    reward: {
      unlockScore: 12000,
      area: '太良町',
      headline: '縮みにくい大粒のカキを焼く',
      tip: '太良町はとれたてのカキを焼いて食べる「カキ小屋」の発祥地とされる。カキと一緒に、竹崎カニやイカ、貝類も焼ける。',
      links: searchLinks('太良町', 'カキ焼き'),
      official: [{ label: '太良町「竹崎カキ」', url: 'https://www.town.tara.lg.jp/kanko/_1899/_1904.html' }],
    },
  },
  {
    id: 'yobuko-ika',
    level: 10,
    name: '呼子イカの活造り',
    shortName: 'イカ活造り',
    image: '/assets/foods/saga/10_yobuko_ika.webp',
    radius: 63,
    mass: 8,
    score: 1024,
    description: '透き通ったまま、皿にのる。',
    source:
      'https://www.asobo-saga.jp/articles/detail/b2d97eae-3915-4ae9-b04f-25f35eba3beb',
    icon: { label: 'イカ活造り', shape: 'fish', baseColor: '#f0ece2', accentColor: '#b8a68a' },
    reward: {
      unlockScore: 16000,
      area: '唐津市呼子町',
      headline: 'いけすから上げてすぐ造るので、身が透き通ったまま皿に盛られる。',
      tip: '刺身のあと、残った下足を塩焼きや天ぷらにしてくれることが多い。一杯を二度楽しむ。',
      links: searchLinks('唐津市呼子', 'イカ活造り'),
    },
  },
  {
    id: 'takezaki-kani',
    level: 11,
    name: '竹崎カニ',
    shortName: '竹崎カニ',
    image: '/assets/foods/saga/11_takezaki_kani.webp',
    radius: 70,
    mass: 9.6,
    score: 2048,
    description: '夏はオス、秋からはメス。',
    source: 'https://www.town.tara.lg.jp/kanko/_1899/_1279.html',
    icon: { label: '竹崎カニ', shape: 'fish', baseColor: '#c0543a', accentColor: '#e8c89a' },
    reward: {
      unlockScore: 22000,
      area: '藤津郡太良町',
      headline: '太良町の竹崎地区近海でとれるカニ。全国的にはワタリガニとして知られる。',
      tip: '塩茹でのほか、天ぷら・味噌汁・雑炊にも。夏はオス、秋口からはメスがおいしいとされる。',
      links: searchLinks('太良町', '竹崎カニ'),
    },
  },
  {
    id: 'saga-gyu',
    level: 12,
    name: '佐賀牛',
    shortName: '佐賀牛',
    image: '/assets/foods/saga/12_saga_gyu.webp',
    radius: 78,
    mass: 11.4,
    score: 4096,
    description: '等級を満たしたものだけが、この名を使える。',
    source: 'https://www.pref.saga.lg.jp/chiji/kiji00392064/index.html',
    icon: { label: '佐賀牛', shape: 'feast', baseColor: '#c0443a', accentColor: '#f0e0d0' },
    reward: {
      unlockScore: 30000,
      area: '佐賀県内各地',
      headline: 'JAグループ佐賀の肥育農家が育てた黒毛和種のうち、等級4・5でBMS7以上のものだけ。',
      tip: '基準に満たないものは「佐賀産和牛」として区別される。ステーキやしゃぶしゃぶで。',
      links: searchLinks('佐賀県', '佐賀牛'),
    },
  },
];

/** 得点は 2 ^ level を基準にしている（GAME_TUNING.score で方式変更可能）。 */
export const sagaFoodStage: StageDefinition = {
  regionId: 'saga',
  regionName: '佐賀県',
  mode: 'food',
  title: '佐賀めしマージ',
  theme: {
    primaryColor: '#3f6b8a',
    secondaryColor: '#d9a441',
    accentColor: '#e8c86b',
    backgroundColor: '#141a1e',
  },
  /** 背景ツアー「佐賀めぐり」。軸は時間帯。早朝の呼子から夜の嬉野へ。 */
  scenery: {
    tour: sagaSceneryTour,
  },
  items: attachHulls(FOOD_ITEMS, SAGA_FOOD_HULLS),
};
